import CartBoxItem from '@/src/common/components/elements/Layout/CartBoxItem'
import { getLineItemsForCart } from '@/src/modules/cart/utilities'
import { LoadingOverlay } from '@/src/modules/checkout/forms/StyledComponents'
import { TCartSummaryProps } from '@/src/modules/checkout/utilities/types'
import { useState } from 'react'
import { ClipLoader } from 'react-spinners'
import { ILineItem, TBoxItem } from '@/src/modules/cart/ICart'
import { styled } from '@/src/stitches.config'
import CartItem from '../Layout/CartItem'
import Text from '../Text/Text'
import PromoCodeSection from './PromoCodeBox'
import { BaseContainer } from '@/src/modules/cart/StyledComponents'
import TemporaryShippingInfo from '@/src/modules/checkout/content/TemporaryShippingInfo'
import {
    determineIncentiveTotalTextForSummary,
    determineTotalIncentiveAmount,
} from '@/src/modules/partners/utilities/determinePartnerIncentiveValues'

const CartItemsContainer = styled('div', {
    display: 'flex',
    fd: 'column',
    flex: 1,
    '@sm': {
        maxHeight: '65vh',
        oy: 'auto',
        px: 10,
    },
})

const TotalWrapper = styled('div', {
    display: 'flex',
    jc: 'space-between',
    width: '100%',
    p: '15px 5px 15px',
    borderBottom: '1px solid $mediumGray',
    textTransform: 'capitalize',
    variants: {
        variant: {
            cart: {
                '@media (max-width: 659px)': {
                    mb: 50,
                },
            },
        },
        marginTop: {
            0: {
                mt: 0,
            },
            50: {
                mt: 50,
            },
        },
    },
})

const Label = styled(Text, {
    fontSize: '$6',
    fontWeight: 'bold',
    variants: {
        color: {
            primary: {
                color: '$primary',
            },
            darkest: {
                color: '$darkest',
            },
            success: {
                color: '$success',
            },
        },
    },
    defaultVariants: {
        color: '$darkest',
    },
})

const CartSummary = ({
    cartData,
    removeItemFromCart,
    updatePromoCode,
    showPromoCodeInput,
    partnerData,
}: TCartSummaryProps) => {
    const [promoCode, setPromoCode] = useState<string>()

    if (!cartData) {
        return (
            <LoadingOverlay>
                <ClipLoader color={'black'} loading={true} size={30} />
            </LoadingOverlay>
        )
    }

    const { resolved_data } = cartData
    const lineItems = getLineItemsForCart(cartData)
    const { lineItemsInBoxes, lineItemsNotInBox } = lineItems

    const shippingFee: number = parseFloat(resolved_data?.delivery_total)
    const promoDiscount: number = parseFloat(resolved_data?.promo_code_discount)

    // determine total incentive amount earned
    // filter to only include items with white_label_reward data
    const whiteLabelItems = lineItemsNotInBox?.filter(
        (item) => !!item?.white_label_rewards
    )
    const totalIncentiveAmount = determineTotalIncentiveAmount(whiteLabelItems)

    const {
        text: incentiveTotalText,
        decorator: incentiveDecorator,
        amount: formattedIncentiveTotal,
    } = determineIncentiveTotalTextForSummary(
        partnerData,
        totalIncentiveAmount,
        whiteLabelItems
    )

    return (
        <BaseContainer>
            <CartItemsContainer>
                {lineItemsInBoxes?.map((box: TBoxItem) => {
                    return (
                        <CartBoxItem
                            key={`item-${box.box_uuid}`}
                            box={box}
                            shipping={
                                box.items[0].shipping && {
                                    address:
                                        box.items[0].shipping.details.address,
                                    carrier: box.items[0].shipping.option,
                                }
                            }
                            removeItemFromCart={removeItemFromCart}
                        />
                    )
                })}
                {lineItemsNotInBox?.map((item: ILineItem) => {
                    return (
                        <CartItem
                            key={item.line_item_uuid}
                            cartLineItem={item}
                            removeItemFromCart={removeItemFromCart}
                            shipping={
                                item.shipping && {
                                    address: item.shipping.details.address,
                                    option: item.shipping.option,
                                }
                            }
                            partnerData={partnerData}
                        />
                    )
                })}
            </CartItemsContainer>
            {!!shippingFee && <TemporaryShippingInfo type={'p'} />}

            {showPromoCodeInput && (
                <PromoCodeSection
                    promoCode={promoCode}
                    setPromoCode={setPromoCode}
                    updatePromoCode={updatePromoCode}
                    hasPromoDiscount={!!promoDiscount}
                    errorMessage={resolved_data?.promo_code_error}
                />
            )}

            {!!promoDiscount && (
                <TotalWrapper marginTop={0}>
                    <Label>Promo Discount</Label>
                    <Label color={'success'}>-({`$${promoDiscount}`})</Label>
                </TotalWrapper>
            )}

            {incentiveTotalText && (
                <TotalWrapper marginTop={50}>
                    <Label>{incentiveTotalText}</Label>
                    <Label color={'success'}>
                        {incentiveDecorator}
                        {formattedIncentiveTotal}
                    </Label>
                </TotalWrapper>
            )}
            {!!shippingFee && (
                <TotalWrapper>
                    <Label>Shipping Total</Label>
                    <Label color={'primary'}>{`$${shippingFee.toFixed(
                        2
                    )}`}</Label>
                </TotalWrapper>
            )}
            <TotalWrapper
                variant={'cart'}
                marginTop={!shippingFee && !incentiveTotalText ? 50 : undefined}
            >
                <Label>Order Total</Label>
                <Label
                    color={'primary'}
                >{`$${resolved_data?.order_total}`}</Label>
            </TotalWrapper>
        </BaseContainer>
    )
}

export default CartSummary
